import { GO_ARENA_BLACK_COLOR, GO_ARENA_BOARD_AREA, GO_ARENA_BOARD_SIZE, GO_ARENA_BUTTON_FEATURE_VALUE, GO_ARENA_GAME_OVER_PASS_COUNT, GO_ARENA_KOMI_NORMALIZATION_POINTS, GO_ARENA_KOMI_POINTS, GO_ARENA_MAXIMUM_MOVE_COUNT, GO_ARENA_MINIMUM_PASS_MOVE_COUNT, GO_ARENA_NO_KO_MOVE, GO_ARENA_PASS_ALIVE_MAXIMUM_INTERNAL_POINT_COUNT, GO_ARENA_PASS_ALIVE_REQUIRED_VITAL_REGION_COUNT, GO_ARENA_PASS_MOVE, GO_ARENA_RECENT_MOVE_COUNT, GO_ARENA_MOKA_INPUT_PLANE_COUNT, GO_ARENA_TEACHER_GLOBAL_FEATURE_COUNT, GO_ARENA_TEACHER_SPATIAL_FEATURE_COUNT, GO_ARENA_WHITE_COLOR } from "./constants.ts";
const createGameState = () => ({
  board: new Int8Array(GO_ARENA_BOARD_AREA),
  consecutivePassCount: 0,
  koMove: GO_ARENA_NO_KO_MOVE,
  moveCount: 0,
  moveHistory: [],
  nextColor: GO_ARENA_BLACK_COLOR
});
const copyGameState = gameState => ({
  board: gameState.board.slice(),
  consecutivePassCount: gameState.consecutivePassCount,
  koMove: gameState.koMove,
  moveCount: gameState.moveCount,
  moveHistory: [...gameState.moveHistory],
  nextColor: gameState.nextColor
});
const getAdjacentMoves = move => {
  const row = Math.floor(move / GO_ARENA_BOARD_SIZE);
  const column = move % GO_ARENA_BOARD_SIZE;
  const adjacentMoves = [];
  if (row > 0) {
    adjacentMoves.push(move - GO_ARENA_BOARD_SIZE);
  }
  if (row < GO_ARENA_BOARD_SIZE - 1) {
    adjacentMoves.push(move + GO_ARENA_BOARD_SIZE);
  }
  if (column > 0) {
    adjacentMoves.push(move - 1);
  }
  if (column < GO_ARENA_BOARD_SIZE - 1) {
    adjacentMoves.push(move + 1);
  }
  return adjacentMoves;
};
const getGroup = (board, startingMove) => {
  const color = board[startingMove];
  if (color === 0) {
    return {
      liberties: new Set(),
      stones: []
    };
  }
  const pendingMoves = [startingMove];
  const visitedMoves = new Set();
  const liberties = new Set();
  const stones = [];
  while (pendingMoves.length > 0) {
    const move = pendingMoves.pop();
    if (move === undefined || visitedMoves.has(move)) {
      continue;
    }
    visitedMoves.add(move);
    stones.push(move);
    for (const adjacentMove of getAdjacentMoves(move)) {
      const adjacentColor = board[adjacentMove];
      if (adjacentColor === 0) {
        liberties.add(adjacentMove);
      } else if (adjacentColor === color) {
        pendingMoves.push(adjacentMove);
      }
    }
  }
  return {
    liberties,
    stones
  };
};
const playMove = (gameState, move) => {
  if (move === GO_ARENA_PASS_MOVE) {
    const nextState = copyGameState(gameState);
    nextState.consecutivePassCount += 1;
    nextState.koMove = GO_ARENA_NO_KO_MOVE;
    nextState.moveCount += 1;
    nextState.moveHistory.push(move);
    nextState.nextColor *= -1;
    return nextState;
  }
  if (gameState.board[move] !== 0 || gameState.koMove === move) {
    return null;
  }
  const nextState = copyGameState(gameState);
  nextState.board[move] = gameState.nextColor;
  const capturedMoves = [];
  for (const adjacentMove of getAdjacentMoves(move)) {
    if (nextState.board[adjacentMove] !== -gameState.nextColor) {
      continue;
    }
    const opponentGroup = getGroup(nextState.board, adjacentMove);
    if (opponentGroup.liberties.size > 0) {
      continue;
    }
    for (const capturedMove of opponentGroup.stones) {
      nextState.board[capturedMove] = 0;
      capturedMoves.push(capturedMove);
    }
  }
  const playedGroup = getGroup(nextState.board, move);
  if (playedGroup.liberties.size === 0) {
    return null;
  }
  nextState.consecutivePassCount = 0;
  nextState.koMove = capturedMoves.length === 1 && playedGroup.stones.length === 1 && playedGroup.liberties.size === 1 ? capturedMoves[0] : GO_ARENA_NO_KO_MOVE;
  nextState.moveCount += 1;
  nextState.moveHistory.push(move);
  nextState.nextColor *= -1;
  return nextState;
};
const getLegalMoves = gameState => {
  const legalMoves = [];
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    if (playMove(gameState, move)) {
      legalMoves.push(move);
    }
  }
  legalMoves.push(GO_ARENA_PASS_MOVE);
  return legalMoves;
};
const selectHighestLegalMove = (gameState, logits) => {
  const legalMoves = getLegalMoves(gameState);
  let selectedMove = gameState.moveCount >= GO_ARENA_MINIMUM_PASS_MOVE_COUNT ? GO_ARENA_PASS_MOVE : legalMoves[0];
  let selectedValue = selectedMove === undefined ? Number.NEGATIVE_INFINITY : logits[selectedMove];
  for (const legalMove of legalMoves) {
    if (legalMove === GO_ARENA_PASS_MOVE && gameState.moveCount < GO_ARENA_MINIMUM_PASS_MOVE_COUNT) {
      continue;
    }
    if (logits[legalMove] > selectedValue) {
      selectedMove = legalMove;
      selectedValue = logits[legalMove];
    }
  }
  return selectedMove ?? GO_ARENA_PASS_MOVE;
};
const encodeMokaFeatures = gameState => {
  const features = new Float32Array(GO_ARENA_BOARD_AREA * GO_ARENA_MOKA_INPUT_PLANE_COUNT);
  const visitedMoves = new Set();
  const setFeature = (move, featureIndex, value = 1) => {
    features[move * GO_ARENA_MOKA_INPUT_PLANE_COUNT + featureIndex] = value;
  };
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    const color = gameState.board[move];
    if (color === 0) {
      continue;
    }
    const isCurrentPlayer = color === gameState.nextColor;
    setFeature(move, isCurrentPlayer ? 0 : 1);
    if (visitedMoves.has(move)) {
      continue;
    }
    const group = getGroup(gameState.board, move);
    for (const stoneMove of group.stones) {
      visitedMoves.add(stoneMove);
    }
    const featureIndex = group.liberties.size === 1 ? isCurrentPlayer ? 2 : 3 : group.liberties.size === 2 ? isCurrentPlayer ? 4 : 5 : -1;
    if (featureIndex >= 0) {
      for (const stoneMove of group.stones) {
        setFeature(stoneMove, featureIndex);
      }
    }
  }
  if (gameState.koMove >= 0) {
    setFeature(gameState.koMove, 6);
  }
  const recentFeatureIndexes = [7, 8];
  for (let historyOffset = 1; historyOffset <= recentFeatureIndexes.length; historyOffset += 1) {
    const historyMove = gameState.moveHistory.at(-historyOffset);
    if (historyMove !== undefined && historyMove < GO_ARENA_BOARD_AREA) {
      setFeature(historyMove, recentFeatureIndexes[historyOffset - 1]);
    } else if (historyMove === GO_ARENA_PASS_MOVE) {
      const passFeatureIndex = 8 + historyOffset;
      for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
        setFeature(move, passFeatureIndex);
      }
    }
  }
  const perspectiveKomi = -GO_ARENA_KOMI_POINTS * gameState.nextColor / GO_ARENA_KOMI_NORMALIZATION_POINTS;
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    setFeature(move, 11, perspectiveKomi);
  }
  return features;
};
const encodeTeacherFeatures = gameState => {
  const spatialFeatures = new Float32Array(GO_ARENA_TEACHER_SPATIAL_FEATURE_COUNT * GO_ARENA_BOARD_AREA);
  const globalFeatures = new Float32Array(GO_ARENA_TEACHER_GLOBAL_FEATURE_COUNT);
  const setSpatialFeature = (featureIndex, move, value = 1) => {
    spatialFeatures[featureIndex * GO_ARENA_BOARD_AREA + move] = value;
  };
  const visitedMoves = new Set();
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    setSpatialFeature(0, move);
    const color = gameState.board[move];
    if (color === 0) {
      continue;
    }
    setSpatialFeature(color === gameState.nextColor ? 1 : 2, move);
    if (visitedMoves.has(move)) {
      continue;
    }
    const group = getGroup(gameState.board, move);
    for (const stoneMove of group.stones) {
      visitedMoves.add(stoneMove);
    }
    if (group.liberties.size >= 1 && group.liberties.size <= 3) {
      for (const stoneMove of group.stones) {
        setSpatialFeature(2 + group.liberties.size, stoneMove);
      }
    }
  }
  if (gameState.koMove >= 0) {
    setSpatialFeature(6, gameState.koMove);
  }
  const recentMoves = gameState.moveHistory.slice(-GO_ARENA_RECENT_MOVE_COUNT).reverse();
  for (let recentMoveIndex = 0; recentMoveIndex < recentMoves.length; recentMoveIndex += 1) {
    const recentMove = recentMoves[recentMoveIndex];
    if (recentMove === GO_ARENA_PASS_MOVE) {
      globalFeatures[recentMoveIndex] = 1;
    } else {
      setSpatialFeature(9 + recentMoveIndex, recentMove);
    }
  }
  globalFeatures[5] = -GO_ARENA_KOMI_POINTS * gameState.nextColor / GO_ARENA_KOMI_NORMALIZATION_POINTS;
  globalFeatures[6] = 1;
  globalFeatures[7] = GO_ARENA_BUTTON_FEATURE_VALUE;
  return {
    globalFeatures,
    spatialFeatures
  };
};
const getColorGroups = (board, color) => {
  const groupKeyByMove = new Int16Array(GO_ARENA_BOARD_AREA);
  groupKeyByMove.fill(-1);
  const groups = new Map();
  const visitedMoves = new Set();
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    if (board[move] !== color || visitedMoves.has(move)) {
      continue;
    }
    const stones = getGroup(board, move).stones;
    const groupKey = stones[0];
    groups.set(groupKey, stones);
    for (const stoneMove of stones) {
      visitedMoves.add(stoneMove);
      groupKeyByMove[stoneMove] = groupKey;
    }
  }
  return {
    groupKeyByMove,
    groups
  };
};
const getNonColorRegions = (board, color, groupKeyByMove) => {
  const regions = [];
  const visitedMoves = new Set();
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    if (board[move] === color || visitedMoves.has(move)) {
      continue;
    }
    const pendingMoves = [move];
    const regionMoves = [];
    const borderingGroupKeys = new Set();
    let containsOpponent = false;
    let internalPointCount = 0;
    let vitalGroupKeys = null;
    while (pendingMoves.length > 0) {
      const regionMove = pendingMoves.pop();
      if (regionMove === undefined || visitedMoves.has(regionMove)) {
        continue;
      }
      visitedMoves.add(regionMove);
      regionMoves.push(regionMove);
      containsOpponent ||= board[regionMove] === -color;
      const adjacentGroupKeys = new Set();
      for (const adjacentMove of getAdjacentMoves(regionMove)) {
        if (board[adjacentMove] === color) {
          const groupKey = groupKeyByMove[adjacentMove];
          adjacentGroupKeys.add(groupKey);
          borderingGroupKeys.add(groupKey);
        } else if (!visitedMoves.has(adjacentMove)) {
          pendingMoves.push(adjacentMove);
        }
      }
      if (adjacentGroupKeys.size === 0) {
        internalPointCount += 1;
      }
      if (board[regionMove] === 0) {
        if (vitalGroupKeys === null) {
          vitalGroupKeys = adjacentGroupKeys;
        } else {
          for (const vitalGroupKey of vitalGroupKeys) {
            if (!adjacentGroupKeys.has(vitalGroupKey)) {
              vitalGroupKeys.delete(vitalGroupKey);
            }
          }
        }
      }
    }
    regions.push({
      borderingGroupKeys,
      containsOpponent,
      internalPointCount,
      moves: regionMoves,
      vitalGroupKeys: vitalGroupKeys ?? new Set()
    });
  }
  return regions;
};
const getPassAliveAnalysis = (board, color) => {
  const {
    groupKeyByMove,
    groups
  } = getColorGroups(board, color);
  const regions = getNonColorRegions(board, color, groupKeyByMove);
  const passAliveGroupKeys = new Set(groups.keys());
  const passAliveRegionIndexes = new Set(regions.map((_, regionIndex) => regionIndex));
  while (true) {
    const removedGroupKeys = new Set();
    for (const groupKey of passAliveGroupKeys) {
      let vitalRegionCount = 0;
      for (const regionIndex of passAliveRegionIndexes) {
        if (regions[regionIndex].vitalGroupKeys.has(groupKey)) {
          vitalRegionCount += 1;
        }
      }
      if (vitalRegionCount < GO_ARENA_PASS_ALIVE_REQUIRED_VITAL_REGION_COUNT) {
        removedGroupKeys.add(groupKey);
      }
    }
    if (removedGroupKeys.size === 0) {
      break;
    }
    for (const removedGroupKey of removedGroupKeys) {
      passAliveGroupKeys.delete(removedGroupKey);
    }
    for (const regionIndex of passAliveRegionIndexes) {
      const doesBorderRemovedGroup = [...removedGroupKeys].some(removedGroupKey => regions[regionIndex].borderingGroupKeys.has(removedGroupKey));
      if (doesBorderRemovedGroup) {
        passAliveRegionIndexes.delete(regionIndex);
      }
    }
  }
  return {
    groups,
    passAliveGroupKeys,
    regions
  };
};
const markPassAliveAreaForColor = (board, color, area) => {
  const {
    groups,
    passAliveGroupKeys,
    regions
  } = getPassAliveAnalysis(board, color);
  for (const groupKey of passAliveGroupKeys) {
    for (const stoneMove of groups.get(groupKey) ?? []) {
      area[stoneMove] = color;
    }
  }
  for (const region of regions) {
    const doesBorderNonPassAliveGroup = [...region.borderingGroupKeys].some(groupKey => !passAliveGroupKeys.has(groupKey));
    const isPassAliveRegion = region.internalPointCount <= GO_ARENA_PASS_ALIVE_MAXIMUM_INTERNAL_POINT_COUNT && !doesBorderNonPassAliveGroup && groups.size > 0;
    const isSafeTerritory = !region.containsOpponent && !doesBorderNonPassAliveGroup && groups.size > 0;
    if (isPassAliveRegion || isSafeTerritory) {
      for (const regionMove of region.moves) {
        area[regionMove] = color;
      }
    } else if (!region.containsOpponent && groups.size > 0) {
      for (const regionMove of region.moves) {
        if (area[regionMove] === 0) {
          area[regionMove] = color;
        }
      }
    }
  }
};
const getPassAliveArea = board => {
  const area = new Int8Array(GO_ARENA_BOARD_AREA);
  markPassAliveAreaForColor(board, GO_ARENA_BLACK_COLOR, area);
  markPassAliveAreaForColor(board, GO_ARENA_WHITE_COLOR, area);
  return area;
};
const getAreaScore = gameState => {
  let score = -GO_ARENA_KOMI_POINTS;
  const visitedMoves = new Set();
  for (const color of gameState.board) {
    score += color;
  }
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    if (gameState.board[move] !== 0 || visitedMoves.has(move)) {
      continue;
    }
    const pendingMoves = [move];
    const territoryMoves = [];
    const borderingColors = new Set();
    while (pendingMoves.length > 0) {
      const territoryMove = pendingMoves.pop();
      if (territoryMove === undefined || visitedMoves.has(territoryMove)) {
        continue;
      }
      visitedMoves.add(territoryMove);
      territoryMoves.push(territoryMove);
      for (const adjacentMove of getAdjacentMoves(territoryMove)) {
        const adjacentColor = gameState.board[adjacentMove];
        if (adjacentColor === 0 && !visitedMoves.has(adjacentMove)) {
          pendingMoves.push(adjacentMove);
        } else if (adjacentColor !== 0) {
          borderingColors.add(adjacentColor);
        }
      }
    }
    if (borderingColors.size === 1 && borderingColors.has(GO_ARENA_BLACK_COLOR)) {
      score += territoryMoves.length;
    } else if (borderingColors.size === 1 && borderingColors.has(GO_ARENA_WHITE_COLOR)) {
      score -= territoryMoves.length;
    }
  }
  return score;
};
const removeDeadStones = (gameState, deadMoves) => {
  const nextState = copyGameState(gameState);
  for (const deadMove of deadMoves) {
    if (deadMove < 0 || deadMove >= GO_ARENA_BOARD_AREA) {
      throw new Error(`Dead move must be between 0 and ${GO_ARENA_BOARD_AREA - 1}.`);
    }
    nextState.board[deadMove] = 0;
  }
  return nextState;
};
const getBestImmediateCapture = gameState => {
  const opponentColor = -gameState.nextColor;
  const opponentStoneCount = gameState.board.filter(color => color === opponentColor).length;
  let bestCaptureCount = 0;
  let bestNextGameState = null;
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    const nextGameState = playMove(gameState, move);
    if (!nextGameState) {
      continue;
    }
    const nextOpponentStoneCount = nextGameState.board.filter(color => color === opponentColor).length;
    const captureCount = opponentStoneCount - nextOpponentStoneCount;
    if (captureCount > bestCaptureCount) {
      bestCaptureCount = captureCount;
      bestNextGameState = nextGameState;
    }
  }
  return bestNextGameState;
};
const getAutomaticallyDeadMoves = gameState => {
  const passAliveArea = getPassAliveArea(gameState.board);
  const passDeadMoves = [];
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    if (gameState.board[move] !== 0 && passAliveArea[move] === -gameState.board[move]) {
      passDeadMoves.push(move);
    }
  }
  let aftermathGameState = removeDeadStones(gameState, passDeadMoves);
  aftermathGameState.consecutivePassCount = 0;
  for (let aftermathMoveCount = 0; aftermathMoveCount < GO_ARENA_BOARD_AREA && aftermathGameState.consecutivePassCount < GO_ARENA_GAME_OVER_PASS_COUNT; aftermathMoveCount += 1) {
    const captureGameState = getBestImmediateCapture(aftermathGameState);
    const nextGameState = captureGameState ?? playMove(aftermathGameState, GO_ARENA_PASS_MOVE);
    if (!nextGameState) {
      break;
    }
    aftermathGameState = nextGameState;
  }
  const deadMoves = [...passDeadMoves];
  for (let move = 0; move < GO_ARENA_BOARD_AREA; move += 1) {
    if (gameState.board[move] !== 0 && aftermathGameState.board[move] === 0) {
      deadMoves.push(move);
    }
  }
  return [...new Set(deadMoves)];
};
const isGameOver = gameState => gameState.consecutivePassCount >= GO_ARENA_GAME_OVER_PASS_COUNT || gameState.moveCount >= GO_ARENA_MAXIMUM_MOVE_COUNT;
export { createGameState, encodeMokaFeatures, encodeTeacherFeatures, getAutomaticallyDeadMoves, getAreaScore, getLegalMoves, getPassAliveArea, isGameOver, playMove, removeDeadStones, selectHighestLegalMove };